import type { DefaultValue, EnumDef, FieldType, PrimitiveType, TypeDef, TypeNode } from '../types/model';

export const PRIMITIVES: PrimitiveType[] = ['string', 'int', 'float', 'boolean'];

export const createId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

export const normalizeName = (value: string) => value.trim().toLowerCase();

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const isInteger = (value: unknown): value is number => isNumber(value) && Number.isInteger(value);

export const getArrayValues = (value: DefaultValue): unknown[] => (Array.isArray(value) ? value : []);

export const getStringValues = (value: DefaultValue): string[] =>
  getArrayValues(value).filter((item): item is string => typeof item === 'string');

export const formatFieldTypeLabel = (fieldType: FieldType, types: TypeDef[], enums: EnumDef[]) => {
  if (fieldType.kind === 'primitive') {
    return fieldType.primitive;
  }
  if (fieldType.kind === 'enum') {
    return enums.find((entry) => entry.id === fieldType.enumId)?.name || 'Enum';
  }
  if (fieldType.kind === 'reference') {
    return `${types.find((entry) => entry.id === fieldType.typeId)?.name || 'Type'} Id`;
  }
  return types.find((entry) => entry.id === fieldType.typeId)?.name || 'Type';
};

export const createEmptyModel = () => ({
  version: createId(),
  timestamp: new Date().toISOString(),
  types: [],
  enums: [],
});

export const buildTypeTree = (types: TypeDef[]): TypeNode[] => {
  const nodes = new Map<string, TypeNode>();
  types.forEach((type) => {
    nodes.set(type.id, { type, children: [] });
  });
  const roots: TypeNode[] = [];
  nodes.forEach((node) => {
    const parentId = node.type.parentTypeId;
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNodes = (list: TypeNode[]) => {
    list.sort((a, b) => a.type.name.localeCompare(b.type.name || '', 'en', { sensitivity: 'base' }));
    list.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
};

export const getDescendantIds = (types: TypeDef[], rootId: string): string[] => {
  const childrenMap = new Map<string, string[]>();
  types.forEach((type) => {
    if (!type.parentTypeId) {
      return;
    }
    const existing = childrenMap.get(type.parentTypeId) ?? [];
    existing.push(type.id);
    childrenMap.set(type.parentTypeId, existing);
  });
  const result: string[] = [];
  const stack = [...(childrenMap.get(rootId) ?? [])];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }
    result.push(current);
    const next = childrenMap.get(current);
    if (next) {
      stack.push(...next);
    }
  }
  return result;
};

export const collectFieldNamesInChain = (typesById: Map<string, TypeDef>, startId: string): Set<string> => {
  const names = new Set<string>();
  let current = typesById.get(startId);
  const seen = new Set<string>();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    current.fields.forEach((field) => {
      const key = normalizeName(field.name);
      if (key) {
        names.add(key);
      }
    });
    if (!current.parentTypeId) {
      break;
    }
    current = typesById.get(current.parentTypeId);
  }
  return names;
};

export const collectFieldNamesInSubtree = (types: TypeDef[], rootId: string): Set<string> => {
  const names = new Set<string>();
  const ids = [rootId, ...getDescendantIds(types, rootId)];
  ids.forEach((id) => {
    const type = types.find((entry) => entry.id === id);
    if (!type) {
      return;
    }
    type.fields.forEach((field) => {
      const key = normalizeName(field.name);
      if (key) {
        names.add(key);
      }
    });
  });
  return names;
};
